import React, { useEffect, useState } from 'react';

import * as dotenv from 'dotenv';
import 'moment/locale/es';
import { useSelector, useStore } from 'react-redux';
import 'react-simple-hook-modal/dist/styles.css';
import { useDispatch } from 'redux-react-hook';

import LoadingScreen from './components/Atoms/LoadingScreen/LoadingScreen';
import {
  DEFAULT_LANGUAGE,
  HUBSPOT_WORKFLOW_CHAT_ID,
  TASKER_CHANNEL,
} from './config/constants';
import { useSocketRoom } from './context/WebSocketContext';
import Country from './models/Country';
import HelperAction from './redux/reducers/helper/actions';
import UserAction from './redux/reducers/user/actions';
import Routes from './routes/routes';
import AuthService from './services/AuthService';
import CountryService from './services/CountryService';
import UtilService from './services/UtilService';
import i18n from './utils/i18n';
import setUpInterceptor from './utils/interceptor';

import './App.scss';
import './assets/styles/global.scss';

declare global {
  interface Window {
    _hsq: { push: (callParam: any) => void };
    hsConversationsSettings: any;
  }
}

const App = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const [loadingInitValues, setLoadingInitValues] = useState(true);
  const authUser = useSelector((state: any) => state.user);
  const { localCountry } = useSelector((state: any) => state.helperReducer);
  dotenv.config({ path: '../.env' });

  setUpInterceptor(store);

  useSocketRoom(
    authUser?.id
      ? TASKER_CHANNEL.replace('[USER_ID]', `${authUser.id}`)
      : undefined
  );

  useEffect(() => {
    loadHubspotConfig();

    //Hubspot scripts
    const hubspotScript = document.createElement('script');
    hubspotScript.src = `//js.hs-scripts.com/${HUBSPOT_WORKFLOW_CHAT_ID}.js`;
    hubspotScript.type = 'text/javascript';
    hubspotScript.id = 'hs-script-loader';
    hubspotScript.defer = true;
    hubspotScript.async = true;

    document.body.appendChild(hubspotScript);

    return () => {
      document.body.removeChild(hubspotScript);
    };
  }, []);

  useEffect(() => {
    init();
  }, [dispatch, localCountry, store]);

  const countryFromApiResponse = async () => {
    const availableCountriesResponse =
      await CountryService.getAvailableCountries();
    let countriesAux = [];
    let localCountryAux: Country | undefined = undefined;

    if (availableCountriesResponse.success) {
      countriesAux = availableCountriesResponse.data;
    } else {
      console.error(availableCountriesResponse.errorMessage);
    }
    dispatch(
      HelperAction.save({ countries: countriesAux, localCountry: localCountry })
    );

    if (!localCountry?.code) {
      const localCountryCode = await UtilService.getLocalCountryFromApi();
      localCountryAux = countriesAux.find(
        (country: Country) =>
          country.code.toUpperCase() === localCountryCode.toUpperCase()
      );
      dispatch(
        HelperAction.save({
          countries: countriesAux,
          localCountry: localCountryAux as Country,
        })
      );
    }

    if (authUser.id === 0) {
      await i18n.changeLanguage(localCountryAux?.language || DEFAULT_LANGUAGE);
    } else {
      await i18n.changeLanguage(authUser.language);
    }
  };

  const verifyUserSession = async () => {
    const user = await AuthService.verifyUserSession(store);

    if (user.success) {
      dispatch(UserAction.save(user.data));
      const interval = setInterval(() => {
        try {
          if (window._hsq) {
            window._hsq.push(['identify', { email: user.data.email }]);
            loadHubspotConfig(user.data);
            clearInterval(interval);
          }
        } catch (error) {
          console.error('hsq app', error);
        }
      }, 100);
    } else {
      dispatch(UserAction.remove({}));
    }
  };

  const loadHubspotConfig = (user?: any) => {
    if (user) {
      window.hsConversationsSettings = {
        loadImmediately: false,
        enableWidgetCookieBanner: false,
        disableAttachment: false,
        identificationEmail: user.email,
        identificationToken: user.hubspotVisitorToken || '',
      };
    } else {
      window.hsConversationsSettings = {
        loadImmediately: false,
        enableWidgetCookieBanner: false,
        disableAttachment: false,
      };
    }
  };

  const init = async () => {
    await verifyUserSession();
    await countryFromApiResponse();
    setLoadingInitValues(false);
  };

  return (
    <>{!loadingInitValues ? <Routes auth={authUser} /> : <LoadingScreen />}</>
  );
};

export default App;
